import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, CheckCircle, XCircle, ShieldCheck, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://pay.mojomoney.africa';
const API = `${BACKEND_URL}/api`;

const TIERS = ['T0', 'T1', 'T2', 'T3'];

const AdminKycReviewPage = () => {
  const navigate = useNavigate();
  const [queue, setQueue] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selected, setSelected] = useState(null);  // doc + user
  const [tierInfo, setTierInfo] = useState(null);  // full state of the selected user
  const [tierChanges, setTierChanges] = useState([]);
  const [reviewReason, setReviewReason] = useState('');
  const [tierTo, setTierTo] = useState('');
  const [tierReason, setTierReason] = useState('');
  const [busy, setBusy] = useState(false);

  // Phase 0/1 — admin auth is via the HttpOnly mojo_session cookie
  // (axios.defaults.withCredentials = true). The dashboard gates on the
  // profile blob in localStorage (set by AdminLogin on successful login).
  // We mirror that gate here so a stale tab without a session bounces
  // straight to the login screen instead of 401-looping.
  const _rawAdminUser = localStorage.getItem('mojo_admin_user');
  let adminUser = null;
  try {
    adminUser = _rawAdminUser && _rawAdminUser !== 'undefined'
      ? JSON.parse(_rawAdminUser)
      : null;
  } catch (_) {
    adminUser = null;
    localStorage.removeItem('mojo_admin_user');
  }
  const legacyToken = localStorage.getItem('mojo_admin_token');  // may be null in cookie-only mode

  useEffect(() => {
    if (!adminUser) {
      navigate('/admin/login');
      return;
    }
    loadQueue('pending');
  }, []);

  // Only include the Bearer header if a legacy token exists; otherwise rely
  // on the session cookie. Never send `Bearer null`.
  const authCfg = useMemo(
    () => legacyToken ? { headers: { Authorization: `Bearer ${legacyToken}` } } : {},
    [legacyToken],
  );

  const loadQueue = async (status) => {
    try {
      const r = await axios.get(`${API}/admin/kyc-queue?status=${status}`, authCfg);
      setQueue(r.data?.items || []);
    } catch (e) {
      toast.error('Failed to load review queue');
    }
  };

  const select = async (entry) => {
    setSelected(entry);
    setReviewReason('');
    setTierTo('');
    setTierReason('');
    try {
      const [tierR, changesR] = await Promise.all([
        axios.get(`${API}/admin/users/${entry.user.id}/kyc-tier`, authCfg),
        axios.get(`${API}/admin/kyc-tier-changes?user_id=${entry.user.id}&limit=10`, authCfg),
      ]);
      setTierInfo(tierR.data);
      setTierChanges(changesR.data?.items || []);
    } catch (e) {
      toast.error('Failed to load user state');
    }
  };

  const review = async (decision) => {
    if (!selected) return;
    if (decision === 'rejected' && !reviewReason.trim()) {
      toast.error('Reason required when rejecting');
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/kyc-documents/${selected.id}/review`,
        { decision, reason: reviewReason || null },
        authCfg,
      );
      toast.success(`Document ${decision}`);
      if (r.data?.suggested_tier) {
        setTierTo(r.data.suggested_tier);
        toast(`User may now be promoted to ${r.data.suggested_tier}`);
      }
      await loadQueue(statusFilter);
      setSelected(null);
      setTierInfo(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Review failed');
    } finally {
      setBusy(false);
    }
  };

  const changeTier = async () => {
    if (!selected || !tierTo) return;
    if (!tierReason.trim()) {
      toast.error('Reason required');
      return;
    }
    setBusy(true);
    try {
      await axios.put(
        `${API}/admin/users/${selected.user.id}/kyc-tier`,
        { to_tier: tierTo, reason: tierReason },
        authCfg,
      );
      toast.success(`Tier changed to ${tierTo}`);
      const [tierR, changesR] = await Promise.all([
        axios.get(`${API}/admin/users/${selected.user.id}/kyc-tier`, authCfg),
        axios.get(`${API}/admin/kyc-tier-changes?user_id=${selected.user.id}&limit=10`, authCfg),
      ]);
      setTierInfo(tierR.data);
      setTierChanges(changesR.data?.items || []);
      setTierReason('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Tier change failed');
    } finally {
      setBusy(false);
    }
  };

  const fileUrl = selected ? `${API}/admin/kyc-documents/${selected.id}/file` : '';

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/admin/dashboard')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to admin
        </Button>

        <div className="flex items-center gap-3 mb-4">
          <ShieldCheck className="w-6 h-6 text-emerald-600" />
          <h1 className="text-2xl font-bold">KYC Review</h1>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-sm text-slate-600">Filter:</span>
            <Select
              value={statusFilter}
              onValueChange={(v) => { setStatusFilter(v); loadQueue(v); setSelected(null); setTierInfo(null); }}
            >
              <SelectTrigger className="w-40" data-testid="kyc-queue-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* Queue */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-base">Queue ({queue.length})</CardTitle>
              <CardDescription>Click a row to review the document.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 max-h-[70vh] overflow-y-auto">
              {queue.length === 0 ? (
                <p className="text-sm text-slate-500">Nothing in the {statusFilter} queue.</p>
              ) : queue.map((entry) => (
                <button
                  key={entry.id}
                  onClick={() => select(entry)}
                  className={`w-full text-left p-3 rounded-md border ${selected?.id === entry.id ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:bg-slate-50'}`}
                  data-testid={`kyc-queue-row-${entry.id}`}
                >
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-slate-500" />
                    <span className="font-medium text-sm">{entry.user?.full_name || '(no name)'}</span>
                    <span className="text-xs text-slate-500">{entry.user?.email}</span>
                  </div>
                  <div className="text-xs text-slate-600 mt-1 flex items-center gap-2">
                    <span className="font-mono">{entry.doc_type}</span>
                    <span>·</span>
                    <span>tier {entry.user?.kyc_tier}</span>
                    <span>·</span>
                    <span>{new Date(entry.uploaded_at).toLocaleString()}</span>
                  </div>
                </button>
              ))}
            </CardContent>
          </Card>

          {/* Detail */}
          <Card className="lg:col-span-3">
            {!selected ? (
              <CardContent className="py-12 text-center text-slate-500">
                Pick a document from the queue to review.
              </CardContent>
            ) : (
              <>
                <CardHeader>
                  <CardTitle className="text-base">
                    {selected.user.full_name} · {selected.doc_type}
                  </CardTitle>
                  <CardDescription>
                    Tier: {selected.user.kyc_tier} · {selected.user.email}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="border rounded-md overflow-hidden bg-slate-100">
                    {selected.mime_type === 'application/pdf' ? (
                      <embed src={fileUrl + `?_=${Date.now()}`} type="application/pdf" className="w-full h-[400px]" />
                    ) : (
                      <img
                        src={fileUrl + `?_=${Date.now()}`}
                        alt={selected.doc_type}
                        className="w-full max-h-[400px] object-contain"
                        data-testid="kyc-doc-preview"
                      />
                    )}
                  </div>

                  <div>
                    <label className="text-sm font-medium">Reviewer note (required to reject)</label>
                    <Textarea
                      value={reviewReason}
                      onChange={(e) => setReviewReason(e.target.value)}
                      placeholder="Why are you rejecting this? Why does it pass?"
                      rows={2}
                      data-testid="kyc-review-reason"
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={() => review('approved')}
                      disabled={busy || selected.status === 'approved'}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="kyc-approve-btn"
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Approve
                    </Button>
                    <Button
                      onClick={() => review('rejected')}
                      disabled={busy || selected.status === 'rejected'}
                      variant="destructive"
                      data-testid="kyc-reject-btn"
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Reject
                    </Button>
                  </div>

                  {tierInfo && (
                    <div className="border-t pt-4">
                      <p className="text-sm font-semibold mb-2">Tier change</p>
                      <div className="text-xs text-slate-600 mb-2 flex flex-wrap gap-3">
                        <span>phone: {tierInfo.phone_verified ? '✓' : '—'}</span>
                        <span>biometric: {tierInfo.biometric_verified ? '✓' : '—'}</span>
                        <span>id_doc: {tierInfo.has_approved_id_doc ? '✓' : '—'}</span>
                        <span>selfie: {tierInfo.has_approved_selfie_doc ? '✓' : '—'}</span>
                        <span>poa: {tierInfo.has_approved_poa_doc ? '✓' : '—'}</span>
                        <span>sof: {tierInfo.has_approved_sof_doc ? '✓' : '—'}</span>
                      </div>
                      <div className="flex gap-2 items-center">
                        <Select value={tierTo} onValueChange={setTierTo}>
                          <SelectTrigger className="w-32" data-testid="kyc-tier-to">
                            <SelectValue placeholder="To…" />
                          </SelectTrigger>
                          <SelectContent>
                            {TIERS.filter((t) => t !== tierInfo.kyc_tier).map((t) => (
                              <SelectItem key={t} value={t}>{t}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          placeholder="Reason (required)"
                          value={tierReason}
                          onChange={(e) => setTierReason(e.target.value)}
                          data-testid="kyc-tier-reason"
                        />
                        <Button
                          onClick={changeTier}
                          disabled={busy || !tierTo}
                          data-testid="kyc-tier-submit"
                        >
                          Change tier
                        </Button>
                      </div>

                      {tierChanges.length > 0 && (
                        <div className="mt-4">
                          <p className="text-xs font-semibold text-slate-600 mb-1">Recent tier changes</p>
                          <ul className="text-xs text-slate-600 space-y-1">
                            {tierChanges.map((c) => (
                              <li key={c.id} className="font-mono">
                                {new Date(c.created_at).toLocaleString()} · {c.from_tier} → {c.to_tier} · {c.reason}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminKycReviewPage;
